import React, { useCallback, useEffect, useMemo } from 'react';
import { useSharedValue } from 'react-native-reanimated';

import { PlayerProjectionFragment } from '@/api/events/query.generated';
import { Box } from '@/components/lib/components';
import BetrAnalytics from '@/feature/analytics/analytics';
import {
    getShouldShowPlayerRecentStats,
    hasPlayerStatsAvailable,
} from '@/feature/player-profile-sheets/utils/player-recent-stats-utils';
import { usePrevious } from '@/hooks/use-previous';
import { MarketOption, MarketStatus } from '@/types/api.generated';
import { nflFullSeason } from '@/utils/league';
import { sortAndGroupProjectionsByStat, sortProjections, sortPromoProjections } from '@/utils/sortProjections';

import { AnalyticsEvent } from '../analytics/constants';
import { betslipPlayerSelector, useBetslipStore } from '../betslip-pickem/hooks/use-betslip-store';
import { allPlayerPropsSelector, usePlayerPropsStore } from '../betslip-pickem/hooks/use-player-props-store';
import { PlayerWithTeamAndEvent } from '../betslip-pickem/types';
import { createPlayerEventKey } from '../betslip-pickem/utils/markets-cache-store-utils';
import { usePickSelection } from '../lobby/hooks/use-pick-selection';
import { checkIfPromoProjection } from '../lobby/utils/lineupsUtils';
import { POPULAR_PLAYERS_FILTER } from '../lobby/utils/players';
import { AlternateProjectionValues } from './AlternateProjectionValues';
import { DiscountedProjectionCard } from './DiscountedProjectionCard';
import { usePlayerProfileDataStore } from './hooks/use-player-profile-data-store';

export const ProjectionsList = ({
    playerWithTeamAndEvent,
    analyticsTag,
}: {
    playerWithTeamAndEvent: PlayerWithTeamAndEvent;
    analyticsTag?: string;
}) => {
    const { event, ...player } = playerWithTeamAndEvent;

    const projections = usePlayerPropsStore(allPlayerPropsSelector(player.id, event.id));
    const playerProjectionFilter = usePlayerProfileDataStore(state => state.selectedPlayerProjectionFilter);
    const popularPlayerProjection = usePlayerProfileDataStore(state => state.popularPlayerProjection);

    const groupedProjections = useMemo(() => {
        const sortedProjections = projections.toSorted(sortProjections);
        const filteredProjections = sortedProjections.filter(projection => !checkIfPromoProjection(projection.type));
        const projectionFilter =
            playerProjectionFilter === POPULAR_PLAYERS_FILTER ? popularPlayerProjection : playerProjectionFilter;

        return sortAndGroupProjectionsByStat(filteredProjections, projectionFilter);
    }, [playerProjectionFilter, popularPlayerProjection, projections]);

    const availableGroupedProjections: Record<string, PlayerProjectionFragment[]> = useMemo(() => {
        return Object.entries(groupedProjections).reduce((acc, [groupKey, groupProjections]) => {
            const allSuspended = groupProjections.every(
                projection => projection.marketStatus === MarketStatus.Suspended
            );

            if (allSuspended) {
                // Keep entire group of projections as is (for UI suspended state on row)
                acc[groupKey] = groupProjections;
            } else {
                // Filter out suspended projections from this group and show only opened ones
                const activeProjections = groupProjections.filter(
                    projection => projection.marketStatus !== MarketStatus.Suspended
                );

                if (activeProjections.length > 0) {
                    acc[groupKey] = activeProjections;
                }
            }

            return acc;
        }, {} as Record<string, PlayerProjectionFragment[]>);
    }, [groupedProjections]);

    const promoProjections = projections
        .filter(projection => checkIfPromoProjection(projection.type))
        .sort(sortPromoProjections);

    const eventId = event.id;
    const playerPicked = useBetslipStore(
        betslipPlayerSelector({
            eventId,
            playerId: player.id,
        })
    );
    const { removeSelection, makeSelection } = usePickSelection();
    const betslip = useBetslipStore(state => state.betslip);
    const prevPlayer = usePrevious(player.id);
    const nflFullSeasonPlayer = nflFullSeason(player.league);

    const initialExpandedIndex = useMemo(() => {
        // always expand the first projection group if
        // it has multiple projection types or player has recent stats for projection group and player is not NFL full season
        // and at least one is not suspended
        const projectionKeys = Object.keys(availableGroupedProjections);
        if (projectionKeys.length === 0) return -1;

        const firstProjection = projectionKeys[0];
        const firstProjectionGroup = availableGroupedProjections[firstProjection] || [];

        const allSuspended = firstProjectionGroup.every(p => p.marketStatus === MarketStatus.Suspended);

        const availablePlayerRecentStats = getShouldShowPlayerRecentStats({
            projectionsGroup: firstProjectionGroup,
            league: player.league,
        });
        const hasMultipleProjectionTypes = firstProjectionGroup.length > 1;
        const multipleProjTypesOrAvailablePlayerStats = hasMultipleProjectionTypes || availablePlayerRecentStats;

        if (multipleProjTypesOrAvailablePlayerStats && !allSuspended) {
            return 0;
        }
        return -1;
    }, [availableGroupedProjections, player.league]);
    const expandedIndex = useSharedValue<number>(-1);

    useEffect(() => {
        if (prevPlayer !== player.id) {
            // When opening a single player profile, the expandedIndex would keep it's state
            // even when opening a different player. This ensures we update it correctly.
            expandedIndex.value = initialExpandedIndex;
        }
    }, [expandedIndex, initialExpandedIndex, player.id, prevPlayer]);

    const removePlayer = useCallback(() => {
        if (eventId && player) {
            removeSelection([{ eventId, player: player }]);
        }
    }, [eventId, player, removeSelection]);

    const selectMarketOption = useCallback(
        (projection: PlayerProjectionFragment, option: MarketOption) => {
            const successfulSelection = makeSelection(
                {
                    player,
                    eventId,
                    outcome: option.outcome,
                    projection,
                },
                analyticsTag
            );
            if (successfulSelection) {
                if (analyticsTag && betslip.length === 1) {
                    BetrAnalytics.trackEvent(AnalyticsEvent.ADD_1ST_PICK, { location: analyticsTag });
                }
            }
        },
        [makeSelection, player, eventId, analyticsTag, betslip.length]
    );

    if (Object.keys(availableGroupedProjections).length === 0 && promoProjections.length === 0) {
        return null;
    }

    const hideExpandButton = Object.keys(availableGroupedProjections).every(projectionKey => {
        const noRecentPlayerStats = !hasPlayerStatsAvailable({
            projections: availableGroupedProjections[projectionKey],
        });
        const noAlternateLines = availableGroupedProjections[projectionKey].length === 1;
        const allProjectionsSuspended = availableGroupedProjections[projectionKey].every(
            it => it.marketStatus === MarketStatus.Suspended
        );

        // NFL full season players: hide if no alternate lines
        if (nflFullSeasonPlayer) {
            return noAlternateLines;
        } else {
            // Always hide expand for suspended projections
            if (allProjectionsSuspended) {
                //Hide expand when all projections are suspended
                return true;
            }
            // Default: hide if single line and no recent stats
            return noAlternateLines && noRecentPlayerStats;
        }
    });

    return (
        <Box overflow={'hidden'}>
            {promoProjections.map(projection => {
                return (
                    <DiscountedProjectionCard
                        projection={projection}
                        event={event}
                        key={projection.marketId}
                        playerPicked={playerPicked}
                        removePlayer={removePlayer}
                        selectMarketOption={selectMarketOption}
                    />
                );
            })}
            {Object.keys(availableGroupedProjections).map((projectionType, index) => {
                const projectionGroup = availableGroupedProjections[projectionType];
                if (projectionGroup.length === 0) {
                    return null;
                }
                return (
                    <AlternateProjectionValues
                        key={`${projectionType}-${createPlayerEventKey(player.id, eventId)}`}
                        testID={`projection-row-${projectionType}`}
                        league={player.league}
                        sport={player.sport}
                        projectionsGroup={projectionGroup}
                        index={index}
                        hideExpandButton={hideExpandButton}
                        expandedIndex={expandedIndex}
                        groupsCount={Object.keys(availableGroupedProjections).length}
                        removePlayer={removePlayer}
                        selectMarketOption={selectMarketOption}
                        playerPicked={playerPicked}
                    />
                );
            })}
        </Box>
    );
};
